import { ChevronDownIcon } from '@chakra-ui/icons';
import {
  Alert,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertIcon,
  Badge,
  Box,
  Button,
  Center,
  Flex,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useColorModeValue,
  Avatar,
  Image,
  Input
} from '@chakra-ui/react';
import { ColumnFiltersState, createColumnHelper, flexRender, getCoreRowModel, getFilteredRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import { VendorProductFlat, vendorRepository } from 'api/vendorRepository';
import { useAuth } from "contexts/authContext";
import * as React from 'react';
import { useState } from 'react';
import { IoMdAdd } from 'react-icons/io';
import { MdDelete, MdEdit } from 'react-icons/md';
import { NavLink } from 'react-router-dom';
import UpdateProductModal from '../EditProduct';
import { IoMdArrowUp } from "react-icons/io";
import { Dispatch, SetStateAction } from 'react';


type ProductTableProps = {
  tableData: VendorProductFlat[];
  onDelete?: (id: string) => void;
  onProductUpdated: () => void;

  allType: string[];
  allVendor: string[];
  vendorFilter?: string;
  typeFilter?: string;
  searchFilter?: string;
  appliedSearch?: string;
  setVendorFilter?: Dispatch<SetStateAction<string>>;
  setTypeFilter?: Dispatch<SetStateAction<string>>;
  setSearchFilter?: Dispatch<SetStateAction<string>>;
  setAppliedSearch?: Dispatch<SetStateAction<string>>;
  initialStatusFilter?: string;
  setPageIndex?: Dispatch<SetStateAction<number>>;
};

export default function CatalogTable({
  tableData,
  onDelete,
  onProductUpdated,
  initialStatusFilter,
  searchFilter,
  setSearchFilter,
  appliedSearch,
  setAppliedSearch,
  vendorFilter,
  setVendorFilter,
  typeFilter,
  setTypeFilter,
  allType,
  allVendor,
  setPageIndex
}: ProductTableProps) {

  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const tableRowBg = useColorModeValue('white', 'navy.800');
  const tableHoverBg = useColorModeValue('blue.50', 'whiteAlpha.50');
  const cellColor = useColorModeValue('secondaryGray.900', 'white');
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [frequencyFilter, setFrequencyFilter] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState('');
  const { role } = useAuth();

  // Centered Alert states
  const [centerAlertVisible, setCenterAlertVisible] = React.useState(false);
  const [centerAlertMessage, setCenterAlertMessage] = React.useState('');
  const [centerAlertStatus, setCenterAlertStatus] = React.useState<'success' | 'error' | 'warning'>('success');
  const [exportAlertVisible, setExportAlertVisible] = useState(false);

  const showCenteredAlert = (message: string, status: 'success' | 'error' | 'warning') => {
    setCenterAlertMessage(message);
    setCenterAlertStatus(status);
    setCenterAlertVisible(true);
    setTimeout(() => setCenterAlertVisible(false), 2500);
  };

  // Edit modal state
  const [selectedProductId, setSelectedProductId] = React.useState<string | null>(null);
  const [selectedVendorProductId, setSelectedVendorProductId] = React.useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const handleEditClick = (productId: string, vendorProductId: string) => {
    setSelectedProductId(productId);
    setSelectedVendorProductId(vendorProductId);
    setIsModalOpen(true);
  };


  // Delete AlertDialog state
  const [deleteVendorProductId, setDeleteVendorProductId] = React.useState<VendorProductFlat['vendorProductId'] | null>(null);
  const [deleteProduct, setDeleteProduct] = React.useState<string | null>(null);
  const [cancelRef] = React.useState(React.createRef<HTMLButtonElement>());
  const doDelete = async () => {
    if (!deleteVendorProductId || !deleteProduct) {
      return;
    }

    try {
      await vendorRepository.deleteVendorProduct(
        deleteVendorProductId,
        deleteProduct
      );

      showCenteredAlert(
        "Product deleted successfully",
        "success"
      );

      onDelete?.(deleteProduct);

      setDeleteProduct(null);
      setDeleteVendorProductId(null);

    } catch (err: any) {
      const errorMessage =
        err?.response?.data ||
        err?.response?.data?.message ||
        "Failed to delete product";

      showCenteredAlert(errorMessage, "error");

      setDeleteProduct(null);
      setDeleteVendorProductId(null);
    }
  };

  const headerTextStyle = {
    fontSize: '14px',
    fontWeight: '700',
    letterSpacing: '0.6px',
    textTransform: 'uppercase' as const,
    color: 'gray.400',
  };

  const cellTextStyle = {
    fontSize: '15px',
    fontWeight: '500',
    color: cellColor,
  };

  const updateColumnFilter = (id: string, value: string) => {
    setColumnFilters(prev => {
      const others = prev.filter(f => f.id !== id);
      if (value) return [...others, { id, value }];
      return others;
    });
  };

  const handleFilterChange = (setter: any, value: string) => {
    setter((prev: string) => {
      if (prev !== value) {
        setPageIndex(0);
        return value;
      }
      return prev;
    });
  };

  const data = React.useMemo(() => {
    let filteredData = tableData;
    if (vendorFilter) filteredData = filteredData.filter(product => product.vendorName === vendorFilter);
    if (typeFilter) filteredData = filteredData.filter(product => product.productType === typeFilter);
    if (statusFilter) filteredData = filteredData.filter(product => product.productStatus === statusFilter);
    return filteredData;
  }, [tableData, vendorFilter, typeFilter, statusFilter]);


  const columnHelper = createColumnHelper<VendorProductFlat>();

  const columns = [
    columnHelper.accessor('productName', {
      header: () => <Text sx={headerTextStyle}>Name</Text>,
      cell: info => {
        const product = info.row.original;
        const imageUrl = product.productImage?.startsWith("http")
          ? product.productImage
          : `http://localhost:5000/${product.productImage}`;
        return (

          <Flex align="center" gap={3}>
            <Image
              src={imageUrl}
              alt={product.productName}
              boxSize="40px"
              objectFit="cover"
              borderRadius="50px"
              fallbackSrc="/assets/images/product-placeholder.png"
              onError={() => console.log("Image failed:", imageUrl)}
            />

            <Text sx={cellTextStyle}>
              {product.productName}
            </Text>
          </Flex>
        );
      },
    }),
    columnHelper.accessor('productType', {
      header: () => <Text sx={headerTextStyle} >Type</Text>,
      cell: info => <Text sx={cellTextStyle}>{info.getValue()}</Text>,
    }),

    ...(role !== 'Vendor'
      ? [columnHelper.accessor('vendorName', {
        header: () => <Text sx={headerTextStyle}>Vendor Name</Text>,
        cell: info => <Text sx={cellTextStyle}>{info.getValue()}</Text>,
      })]
      : []),
    columnHelper.accessor('hasAMC', {
      header: () => <Text sx={headerTextStyle} >AMC</Text>,
      cell: info => {
        const value = info.getValue();
        return (
          <Badge
            colorScheme={value ? 'green' : 'red'}
            fontSize='15px'
            fontWeight='500'
          >
            {value ? 'Yes' : 'No'}
          </Badge>
        );
      },
    }),

    columnHelper.accessor('productStatus', {
      header: () => <Text sx={headerTextStyle} >Status</Text>,
      cell: info => {
        const value = info.getValue();
        let colorScheme = value === 'Active' ? 'green' : value === 'Inactive' ? 'red' : value === 'Under Maintainance' ? 'blue' : 'red';
        return <Badge colorScheme={colorScheme} fontSize='15px' fontWeight='500'>{value}</Badge>;
      },
    }),

    ...(role && (role === "Admin" || role === "Vendor") ? [columnHelper.display({
      id: 'actions',
      header: () => <Text sx={headerTextStyle} >Actions</Text>,
      cell: ({ row }) => {
        return (
          <Flex align="center" minWidth="40px">
            <HStack spacing={2}>
              <Tooltip label="Edit Product" placement="top" hasArrow>
                <IconButton
                  aria-label='Edit Product'
                  icon={<MdEdit size={20} />}
                  variant='ghost'
                  color="blue.500"
                  _hover={{ bg: 'blue.100', color: 'blue.600' }}
                  _active={{ bg: 'blue.100' }}
                  rounded="full"
                  onClick={() => handleEditClick(
                    row.original.productId,
                    row.original.vendorProductId
                  )}
                />

              </Tooltip>

              <Tooltip label="Delete Product" placement="top" hasArrow>
                <IconButton
                  aria-label='Delete'
                  icon={<MdDelete size={20} />}
                  variant='ghost'
                  color="red.500"
                  _hover={{ bg: 'red.100', color: 'red.600' }}
                  _active={{ bg: 'red.100' }}
                  rounded="full"
                  onClick={() => {
                    setDeleteProduct(row.original.productId);
                    setDeleteVendorProductId(row.original.vendorProductId);
                  }}

                />
              </Tooltip>

            </HStack>
          </Flex>
        );
      },
    })] : []),
  ];
  const bgColor = useColorModeValue("gray.100", "gray.700");
  const table = useReactTable({
    data,
    columns,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <Flex direction="column" w="100%" p={4} overflowX="auto">
      {/* Table & buttons */}
      <Box w='100%' p={4} flex="1" overflowX="auto">

        <Flex justify="space-between" align="center" mb={4} wrap="nowrap" gap={2}>
          <HStack spacing={3} flex="1" minW="300px">
            <Input
              color={"white"}
              placeholder="Search..."
              value={searchFilter}
              onChange={(e) => {
                const value = e.target.value;
                setSearchFilter(value);

                if (value.trim() === "") {
                  setAppliedSearch("");
                }
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setAppliedSearch(searchFilter);
                }
              }}
              bg={useColorModeValue("gray.100", "gray.700")}
              width="30%"
              borderRadius="full"
            />

            {/* Vendor Filter */}
            {(role === "Admin" || role === "OrgAdmin") && (
              <Menu>
                <MenuButton as={Button} rightIcon={<ChevronDownIcon />} bg={bgColor} borderRadius="full">
                  {vendorFilter || "Vendor"}
                </MenuButton>
                <MenuList>
                  <MenuItem onClick={() => { setVendorFilter(''); handleFilterChange(setVendorFilter, ''); }}>All</MenuItem>
                  {allVendor.map(v => (
                    <MenuItem key={v} onClick={() => { setVendorFilter(v); handleFilterChange(setVendorFilter, v); }}>{v}</MenuItem>
                  ))}
                </MenuList>
              </Menu>
            )}
            {/* Type Filter */}
            <Menu>
              <MenuButton as={Button} rightIcon={<ChevronDownIcon />} bg={useColorModeValue("gray.100", "gray.700")} borderRadius="full">
                {typeFilter || "Type"}
              </MenuButton>
              <MenuList>
                <MenuItem onClick={() => { setTypeFilter(''); handleFilterChange(setTypeFilter, ''); }}>All</MenuItem>
                {allType.map(t => (
                  <MenuItem key={t} onClick={() => { setTypeFilter(t); handleFilterChange(setTypeFilter, t); }}>{t}</MenuItem>
                ))}
              </MenuList>
            </Menu>

            {/* Status Filter */}
            <Menu>
              <MenuButton
                as={Button}
                rightIcon={<ChevronDownIcon />}
                bg={useColorModeValue("gray.100", "gray.700")}
                borderRadius="full"
              >
                {statusFilter || "Status"}
              </MenuButton>
              <MenuList>
                <MenuItem onClick={() => { setStatusFilter(''); updateColumnFilter('status', ''); }}> All</MenuItem>
                <MenuItem onClick={() => { setStatusFilter('Active'); updateColumnFilter('status', 'Active'); }}>Active</MenuItem>
                <MenuItem onClick={() => { setStatusFilter('Inactive'); updateColumnFilter('status', 'Inactive'); }}> Inactive </MenuItem>
              </MenuList>
            </Menu>
          </HStack>
          <HStack spacing={3}>
            {(role === "Admin" || role === "OrgAdmin" || role === "Vendor") && (
              <NavLink to="/admin/vendors/VendorAddProduct">
                <Button colorScheme="blue" borderRadius="16px">
                  <IoMdAdd /> Add
                </Button>
              </NavLink>
            )}
          </HStack>
        </Flex>

        {/* Table */}
        <Box >
          <Table variant="simple" color="gray.500">
            <Thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <Tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <Th key={header.id} borderColor={borderColor}>
                      {!header.isPlaceholder && (
                        <Flex
                          onClick={header.column.getToggleSortingHandler()}
                          cursor="pointer"
                          justify="space-between"
                          align="center"
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </Flex>
                      )}
                    </Th>
                  ))}
                </Tr>
              ))}
            </Thead>
            <Tbody>
              {table.getRowModel().rows.map((row) => (
                <Tr
                  key={row.id}
                  sx={{
                    '& td': {
                      borderBottom: '1px solid',
                      borderColor: borderColor,
                      background: tableRowBg,
                      cursor: 'pointer',
                    },
                    '&:hover td': {
                      backgroundColor: tableHoverBg,
                    },
                  }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <Td key={cell.id} borderColor={borderColor} minW="150px">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </Td>
                  ))}
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>

      </Box>


      {/* Edit Modal */}
      <UpdateProductModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        productId={selectedProductId}
        vendorProductId={selectedVendorProductId}
        onProductUpdated={() => {
          showCenteredAlert('Product updated successfully', 'success');
          onProductUpdated();
          setIsModalOpen(false);
        }}
        showCenteredAlert={showCenteredAlert}
      />

      {/* Centered Alert */}
      {exportAlertVisible && (
        <Center
          position="fixed"
          top="0"
          left="0"
          right="0"
          bottom="0"
          zIndex={60}
          pointerEvents="none"
        >
          <Alert
            status="success"
            variant="solid"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            width="600px"
            borderRadius="14px"
            boxShadow="xl"
            py={10}
            px={16}
            pointerEvents="auto"
          >
            <AlertIcon boxSize="40px" mr={0} />
            <Text fontSize="lg" fontWeight="700" mt={1}>
              Exported CSV successfully
            </Text>
          </Alert>
        </Center>
      )}


      {centerAlertVisible && (
        <Center
          position="fixed"
          top="0"
          left="0"
          right="0"
          bottom="0"
          zIndex={60}
          pointerEvents="none"
        >
          <Alert
            status={centerAlertStatus}
            variant={'solid'}
            bg="red.500"
            color="white"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            width="600px"
            borderRadius="14px"
            boxShadow="lg"
            py={10}
            px={16}
            pointerEvents="auto"
          >
            <AlertIcon boxSize="40px" mr={0} color="white" />

            <Text fontSize="lg" fontWeight="700" mt={1}>
              {centerAlertMessage}
            </Text>
          </Alert>
        </Center>
      )
      }

      {/* Delete Confirmation AlertDialog */}
      <AlertDialog
        isOpen={!!deleteProduct}
        leastDestructiveRef={cancelRef}
        onClose={() => {
          setDeleteVendorProductId(null);
          setDeleteProduct(null);
        }}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Product
            </AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to delete this product? This action cannot be undone.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} borderColor="gray.300" onClick={() => setDeleteProduct(null)}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={doDelete} ml={3}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Flex>
  );
}